import { ProportionBar, StatTile } from '../charts/Charts'
import { reportPill } from '../ui'

interface Cycle {
  vehicle_id: string
  cycle_id: string
  started_ts: string
  completeness: string
  total_hours: number | null
  km: number | null
  blended_kwh_per_km: number | null
  regen_ratio_pct: number | null
}

interface DayReconcile {
  day: string
  geo_departures: number
  tims_lrs: number
  delta: number
}

export interface RoundTripData {
  headline: {
    full_laps: number | null
    cycles_seen: number | null
    typical_lap_hours: number | null
    best_1_in_10_hours: number | null
    worst_1_in_10_hours: number | null
    typical_hub_hours: number | null
    typical_hub_hours_not_charging: number | null
    typical_blended_kwh_per_km: number | null
  }
  total_cycles: number
  cycles: Cycle[]
  day_reconcile: DayReconcile[]
}

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'neutral'> = {
  complete: 'ok', full: 'ok', partial: 'warn',
}

export function RoundTripPreview({ data }: { data: RoundTripData }): JSX.Element {
  const h = data.headline
  const partial = h.cycles_seen != null && h.full_laps != null
    ? Math.max(0, h.cycles_seen - h.full_laps) : null

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
        <StatTile label="Full round trips" value={h.full_laps ?? '—'} />
        <StatTile label="Seen, incl. partial" value={h.cycles_seen ?? '—'} />
        <StatTile label="Fastest 1-in-10" value={h.best_1_in_10_hours ?? '—'} unit="h" tone="ok" />
        <StatTile label="Typical" value={h.typical_lap_hours ?? '—'} unit="h" />
        <StatTile label="Slowest 1-in-10" value={h.worst_1_in_10_hours ?? '—'} unit="h" tone="warn" />
        <StatTile label="Typical energy" value={h.typical_blended_kwh_per_km ?? '—'} unit="kWh/km" />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {partial != null && h.full_laps != null && (
          <div className="rounded-lg border border-slate-100 p-3">
            <h5 className="mb-2 text-[11px] font-semibold text-slate-700">Full vs partial round trips</h5>
            <ProportionBar segments={[
              { label: 'Full', value: h.full_laps, color: '#16A34A' },
              { label: 'Partial / in progress', value: partial, color: '#D97706' },
            ]} />
          </div>
        )}
        <div className="rounded-lg border border-slate-100 p-3">
          <h5 className="mb-2 text-[11px] font-semibold text-slate-700">Typical time split, one round trip</h5>
          <ProportionBar segments={[
            { label: 'Hub, charging', value: Math.max(0, (h.typical_hub_hours ?? 0) - (h.typical_hub_hours_not_charging ?? 0)), color: '#2563EB' },
            { label: 'Hub, not charging', value: h.typical_hub_hours_not_charging ?? 0, color: '#D97706' },
            { label: 'Road', value: Math.max(0, (h.typical_lap_hours ?? 0) - (h.typical_hub_hours ?? 0)), color: '#16A34A' },
          ]} />
        </div>
      </div>

      <div className="rounded-lg border border-slate-100">
        <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
          <h5 className="text-[11px] font-semibold text-slate-700">Most recent round trips</h5>
          <span className="text-[10.5px] text-slate-400">
            {data.cycles.length} of {data.total_cycles} shown — full list in the workbook
          </span>
        </div>
        {data.cycles.length === 0 ? (
          <p className="px-3 py-4 text-center text-[11px] text-slate-400">No round trips in this window.</p>
        ) : (
          <table className="w-full text-[11.5px]">
            <thead>
              <tr className="border-b border-slate-100 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                <th className="px-3 py-1.5 text-left">Vehicle</th>
                <th className="px-2 py-1.5 text-left">Started</th>
                <th className="px-2 py-1.5 text-left">Status</th>
                <th className="px-2 py-1.5 text-right">Hours</th>
                <th className="px-2 py-1.5 text-right">km</th>
                <th className="px-2 py-1.5 text-right">kWh/km</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {data.cycles.map((c) => (
                <tr key={c.cycle_id} className="transition-colors duration-100 hover:bg-slate-50/60">
                  <td className="px-3 py-1.5 font-mono font-medium text-slate-700">{c.vehicle_id}</td>
                  <td className="px-2 py-1.5 text-slate-500">{new Date(c.started_ts).toLocaleDateString()}</td>
                  <td className="px-2 py-1.5">
                    <span className={reportPill(STATUS_TONE[c.completeness?.toLowerCase()] ?? 'neutral')}>
                      {c.completeness ?? '—'}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">{c.total_hours ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">{c.km ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">{c.blended_kwh_per_km ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data.day_reconcile.length > 0 && (
        <div className="rounded-lg border border-slate-100">
          <div className="border-b border-slate-100 px-3 py-2">
            <h5 className="text-[11px] font-semibold text-slate-700">Round trips vs TIMS, by day</h5>
            <p className="mt-0.5 text-[10.5px] text-slate-400">
              Both sides bucketed by departure day (IST) — ours from the GPS cycle, TIMS from
              when the LR is keyed. Delta should sit at or above zero.
            </p>
            <p className="mt-1 text-[10.5px] text-slate-400">
              TIMS often logs only one gate scan per LR, sometimes the return rather than the
              departure — that trip lands a day later on the TIMS side than on ours, which can
              swing a single day's delta either way. Our own GPS tracking doesn't depend on a
              manual scan, so it stays the more reliable count as vehicles keep sending data.
            </p>
          </div>
          <table className="w-full text-[11.5px]">
            <thead>
              <tr className="border-b border-slate-100 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                <th className="px-3 py-1.5 text-left">Day</th>
                <th className="px-2 py-1.5 text-right">Ours</th>
                <th className="px-2 py-1.5 text-right">TIMS</th>
                <th className="px-2 py-1.5 text-right">Delta</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {data.day_reconcile.map((r) => (
                <tr key={r.day} className="transition-colors duration-100 hover:bg-slate-50/60">
                  <td className="px-3 py-1.5 font-mono text-slate-700">{r.day}</td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">{r.geo_departures}</td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">{r.tims_lrs}</td>
                  <td className={`px-2 py-1.5 text-right font-mono tabular-nums font-medium ${
                    r.delta < 0 ? 'text-rose-600' : 'text-emerald-600'
                  }`}>
                    {r.delta > 0 ? `+${r.delta}` : r.delta}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
